import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { SymbolKey } from '@/entities/symbol';
import type { CsvWidgetExportSource } from '@/features/data-hub/types/context.types';

export enum DrawerType {
  ASK_GX = 'ASK_GX',
  EXPORT = 'EXPORT'
}

export type CsvWidgetExportContext = {
  source: CsvWidgetExportSource | null;
  dateColumnKey: string | null;
  tabName?: string;
};

export type CustomChartExportContext = {
  metadata: Record<string, object | string>;
  tabName: string;
  selectedRange?: string;
};

export type RestrictedTableExportSymbol = { key: SymbolKey; group: string };

export type RestrictedTableExportContext = {
  tabName: string;
  symbols: RestrictedTableExportSymbol[];
};

export type DrawerState = {
  openDrawer: DrawerType | null;
  showExportHistory: boolean;
  openedFrom?: string;
  csvWidgetExportContext: CsvWidgetExportContext | null;
  customChartExportContext: CustomChartExportContext | null;
  restrictedTableExportContext: RestrictedTableExportContext | null;
};

const initialState: DrawerState = {
  openDrawer: null,
  showExportHistory: false,
  csvWidgetExportContext: null,
  customChartExportContext: null,
  restrictedTableExportContext: null
};

const drawerSlice = createSlice({
  name: 'drawer',
  initialState,
  reducers: {
    openDrawer: (state, action: PayloadAction<DrawerType>) => {
      state.openDrawer = action.payload;
    },
    openExportDrawer: (
      state,
      action: PayloadAction<{
        showHistory?: boolean;
        source?: string;
        csvWidgetExportContext?: CsvWidgetExportContext | null;
        customChartExportContext?: CustomChartExportContext | null;
        restrictedTableExportContext?: RestrictedTableExportContext | null;
      }>
    ) => {
      state.openDrawer = DrawerType.EXPORT;
      state.showExportHistory = action.payload.showHistory ?? false;
      state.openedFrom = action.payload.source;
      state.csvWidgetExportContext = action.payload.csvWidgetExportContext ?? null;
      state.customChartExportContext = action.payload.customChartExportContext ?? null;
      state.restrictedTableExportContext = action.payload.restrictedTableExportContext ?? null;
    },
    setShowExportHistory: (state, action: PayloadAction<boolean>) => {
      state.showExportHistory = action.payload;
    },
    closeDrawer: state => {
      state.openDrawer = null;
      state.showExportHistory = false;
      state.openedFrom = undefined;
      state.csvWidgetExportContext = null;
      state.customChartExportContext = null;
      state.restrictedTableExportContext = null;
    }
  }
});

export const { openDrawer, openExportDrawer, setShowExportHistory, closeDrawer } =
  drawerSlice.actions;
export default drawerSlice.reducer;
